/**
 * declarationVersion — кой номер носи един текст, и на кой номер се е подписал родител.
 *
 * Съществува заради АСИМЕТРИЯ, която иначе ще бъде „изчистена" в грешната посока:
 *
 * | къде липсва версията | значи |
 * |---|---|
 * | `Declaration.version` | **1** — днешното съдържание Е първата версия (Р-Е.10) |
 * | `Signature.declarationVersion` | **не се знае** — и остава така |
 *
 * 🔴 Една и съща липса, противоположни значения. Това е формата, която този
 * проект вече е плащал многократно — две величини под едно име. Изведена на
 * място, тя рано или късно се „уеднаквява": някой слага `?? 1` и на подписа,
 * защото изглежда като недовършено чистене. Оттам подписът започва да твърди,
 * че знаем на какво се е подписал родителят.
 *
 * Затова двете четения имат РАЗЛИЧНИ имена и живеят тук. Никъде другаде `?? 1`.
 *
 * ⚠️ Защо това не е счетоводство: съдържанието на декларациите се редактира през
 * админ панела и живее във Firestore. Подпис без версия доказва, че някой е
 * натиснал бутон, не че е приел конкретен текст — а чл. 7(1) иска второто.
 */

import { toMillisOrNull } from './millis';

/** Първата версия. Днешното съдържание Е тя — не 0 и не „неверсионирано". */
export const FIRST_DECLARATION_VERSION = 1;

/** Минимумът от `Declaration`, нужен за версията. Приема и raw Firestore документ. */
export interface DeclarationVersionFields {
  version?: number | null;
  /**
   * `versionPublishedAt` се пише САМО от `publishDeclarationVersion`.
   *
   * ⚠️ Индексният подпис не е разхлабване, а необходимост: без него TypeScript
   * прилага „weak type detection" и отхвърля истинския `Declaration`, чието
   * `metadata` носи `createdAt`/`updatedAt` и нито едно от обявените тук полета.
   */
  metadata?: { versionPublishedAt?: unknown; [key: string]: unknown } | null;
}

/** Минимумът от `Signature`. */
export interface SignedVersionFields {
  declarationVersion?: number | null;
  /**
   * Кога е била публикувана версията, НА КОЯТО се е подписал родителят.
   *
   * 🔴 Снима се в подписа, защото на декларацията тя не оцелява: там полето е
   * едно и се презаписва при всяко публикуване, тоест винаги описва ПОСЛЕДНАТА
   * версия. Прочетена по-късно от декларацията, „датата на версия 3" всъщност е
   * датата на версия 5 — и се мени всеки път, когато някой публикува.
   */
  declarationVersionPublishedAt?: unknown;
}

/**
 * Версията на едно СЪДЪРЖАНИЕ.
 *
 * Липсва → `FIRST_DECLARATION_VERSION`. Заварените шест декларации нямат полето
 * и това не значи „неизвестно": значи, че текстът, който стои там днес, е
 * версия 1, и следващото публикуване прави 2.
 *
 * ⚠️ Нечисло (низ, `NaN`, отрицателно) се брои за липсващо, а не се пропуска:
 * версията трябва да е СРАВНИМА, иначе подредбата ѝ не носи информация.
 */
export function declarationVersionOf(
  declaration: DeclarationVersionFields | null | undefined,
): number {
  const raw = declaration?.version;
  if (typeof raw !== 'number' || !Number.isInteger(raw) || raw < FIRST_DECLARATION_VERSION) {
    return FIRST_DECLARATION_VERSION;
  }
  return raw;
}

/**
 * Версията, на която се е подписал един родител.
 *
 * 🔴 `undefined`, когато подписът не я носи — и НИКОГА не подразбира. Старите
 * подписи остават без версия, защото това е честният запис.
 */
export function signedVersionOf(
  signature: SignedVersionFields | null | undefined,
): number | undefined {
  const raw = signature?.declarationVersion;
  if (typeof raw !== 'number' || !Number.isInteger(raw) || raw < FIRST_DECLARATION_VERSION) {
    return undefined;
  }
  return raw;
}

/**
 * Кога е публикувана версията, на която се е подписал родителят — в милисекунди.
 *
 * `null`, когато подписът не носи снимка: старите подписи нямат дата и НЕ
 * получават датата на живата декларация назад. Същото правило като при номера.
 */
export function signedVersionPublishedMillis(
  signature: SignedVersionFields | null | undefined,
): number | null {
  return toMillisOrNull(signature?.declarationVersionPublishedAt);
}

/** Следващата версия при публикуване. Монотонна, стъпка 1. */
export function nextDeclarationVersion(
  declaration: DeclarationVersionFields | null | undefined,
): number {
  return declarationVersionOf(declaration) + 1;
}

/**
 * Изостанал ли е подписът спрямо текущото съдържание.
 *
 * `undefined` при подпис без версия — „не се знае" не е нито изостанал, нито
 * актуален, и сливането му с едното от двете е същата грешка като `?? 1`.
 *
 * ⬜ ТУК би влязъл флагът „съдържанието е редактирано след версия N", ако
 * owner-ът приеме отпечатъка на съдържанието (отделно решение, не е част от
 * Р-Е.10). Той би отговарял на друг въпрос — „текстът мръднал ли е БЕЗ
 * публикуване" — и е адитивен спрямо това тук.
 */
export function isSignatureOutdated(
  signature: SignedVersionFields | null | undefined,
  declaration: DeclarationVersionFields | null | undefined,
): boolean | undefined {
  const signed = signedVersionOf(signature);
  if (signed === undefined) return undefined;
  return signed < declarationVersionOf(declaration);
}

/**
 * Кога тази версия е била ПУБЛИКУВАНА, в милисекунди. `null`, когато не се знае.
 *
 * ⚠️ НЕ пада на `metadata.updatedAt`. Тя е датата на последната РЕДАКЦИЯ, а не
 * на публикуване — показана като „публикувана на", би твърдяла, че текстът е
 * обявен тогава. Същият клас като приписването на версия 1 назад на стар подпис:
 * стойността съществува, но отговаря на друг въпрос.
 */
export function versionPublishedMillis(
  declaration: DeclarationVersionFields | null | undefined,
): number | null {
  return toMillisOrNull(declaration?.metadata?.versionPublishedAt);
}

/**
 * ЗАВАРЕНА ли е тази версия — тоест никой не я е публикувал.
 *
 * Шестте живи декларации са версия 1 по решение (Р-Е.10), но никой не е натискал
 * бутона, тоест дата на публикуване няма. Това е различно от „версия 1,
 * публикувана тогава-и-тогава" и екранът трябва да ги различава: иначе липсващата
 * дата изглежда като недовършен запис, вместо като честен.
 */
export function isVersionLegacy(
  declaration: DeclarationVersionFields | null | undefined,
): boolean {
  return versionPublishedMillis(declaration) === null;
}
